/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/order */
import React, { useContext, useEffect, useState } from 'react'
import { Input, Modal, Select, notification } from 'antd'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import TextArea from 'antd/lib/input/TextArea'
import { CloseOutlined } from '@ant-design/icons'
import { TicketNotifContext } from '@contexts'
import { Driver, Note, Observation } from '@types'
import { NoteElement } from '@components'
import { useTicketsById } from '@hooks'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import { TIME_OPTIONS_CONFIG } from '../../../../../constants'

type Props = {
  open: boolean
  onClose: () => void
  onOk: ({ description }: Observation, driver: number, truck: number, trailer: number) => void
  loading: boolean
  requireTruckAndTrailer: boolean
  requireDriver: boolean
  id?: number
}

const AcceptTicketModal: React.FC<Props> = ({
  open,
  onClose,
  onOk,
  loading,
  requireTruckAndTrailer,
  requireDriver,
}) => {
  const { t } = useTranslation()
  const { reset } = useForm({})
  const { ticketById, isTicketByIdLoading } = useTicketsById()
  const [form] = StyledForm.useForm()
  const TicketContext = useContext(TicketNotifContext)
  const [initialValues, setInitialValues] = useState({})
  const [selectedDriver, setSelectedDriver] = useState<Driver>()
  const handleDriverChange = (value: any) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const driver = ticketById?.ticket?.carrier?.drivers?.find(driver => driver.id === value?.value)
    setSelectedDriver(driver)
    if (driver) {
      const preferredTruck = ticketById?.ticket?.carrier?.trucks?.find(
        truck => truck.id === driver.preferredTrucks?.[0]?.id,
      )
      const preferredTrailer = ticketById?.ticket?.carrier?.trailers?.find(
        trailer => trailer.id === driver.preferredTrailers?.[0]?.id,
      )

      if (preferredTruck?.available) {
        form.setFieldsValue({
          truck: {
            label: preferredTruck.equipmentType
              ? `${preferredTruck.licensePlate} - ${preferredTruck.description} - ${preferredTruck.equipmentType.nameShort}`
              : `${preferredTruck.licensePlate} - ${preferredTruck.description}`,
            value: preferredTruck.id,
          },
        })
      }

      if (preferredTrailer?.available) {
        form.setFieldsValue({
          trailer: {
            label: preferredTrailer.equipmentType
              ? `${preferredTrailer.licensePlate} - ${preferredTrailer.description} - ${preferredTrailer.equipmentType.nameShort}`
              : `${preferredTrailer.licensePlate} - ${preferredTrailer.description}`,
            value: preferredTrailer.id,
          },
        })
      }
    }
  }
  useEffect(() => {
    if (ticketById?.ticket) {
      const purchaseOrder = ticketById.ticket.sapItemSupplier?.DocumentNumberID ?? undefined
      const earlyDeliveryDate = ticketById.ticket.sapItemSupplier?.EarlyDeliveryDate
        ? dayjs(new Date(ticketById.ticket.sapItemSupplier?.EarlyDeliveryDate))
            .format('MMM YYYY')
            .toUpperCase()
        : ''

      const purchaseOrderAndEdd = purchaseOrder ? `# ${purchaseOrder} ${earlyDeliveryDate}` : ''
      const availableQuantity = ticketById.ticket.sapItemSupplier?.AvailableQuantity
        ? `${ticketById.ticket.sapItemSupplier?.AvailableQuantity} tn`
        : ''
      const date = dayjs(ticketById.ticket.withdrawalDate)
      const timeOption = TIME_OPTIONS_CONFIG.find(({ intValue }) => intValue === date.hour())
      setInitialValues({
        origin: ticketById.ticket.origin?.tag ?? '',
        date: `${date.format('llll')}${timeOption ? ` ${t(timeOption.labelKey)}` : ''}`,
        supplier: ticketById.ticket.supplier?.businessName ?? '',
        destination: ticketById.ticket.destination?.tag ?? '',
        material: ticketById.ticket.material?.nameSap ?? '',
        equipment: ticketById.ticket.equipment?.name ?? '',
        comments: ticketById.ticket.comments ?? '',
        purchaseOrderAndEdd,
        availableQuantity,
      })
      setTimeout(() => form.resetFields(), 10)
    }
  }, [ticketById, open])
  useEffect(() => {
    if (ticketById && selectedDriver) {
      if (selectedDriver.preferredTruck) {
        const selectedTruck = ticketById?.ticket?.carrier?.trucks?.find(
          truck => truck.id === selectedDriver?.preferredTruck.id,
        )
        if (selectedTruck?.available) {
          form.setFieldsValue({
            truck: {
              label: selectedTruck?.equipmentType
                ? `${selectedTruck?.licensePlate} - ${selectedTruck?.description} - ${selectedTruck?.equipmentType.nameShort}`
                : `${selectedTruck?.licensePlate} - ${selectedTruck?.description}`,
              value: selectedTruck?.id,
            },
          })
        }
      }

      if (selectedDriver.preferredTrailer) {
        const selectedTrailer = ticketById?.ticket?.carrier?.trailers?.find(
          trailer => trailer.id === selectedDriver?.preferredTrailer?.id,
        )
        if (selectedTrailer?.available) {
          form.setFieldsValue({
            trailer: {
              label: selectedTrailer?.equipmentType
                ? `${selectedTrailer?.licensePlate} - ${selectedTrailer?.description} - ${selectedTrailer?.equipmentType.nameShort}`
                : `${selectedTrailer?.licensePlate} - ${selectedTrailer?.description}`,
              value: selectedTrailer?.id,
            },
          })
        }
      }
    }
  }, [selectedDriver])

  return (
    <Modal
      className="accept-ticket-modal"
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        setInitialValues({})
        setTimeout(() => form.resetFields(), 10)
        onClose()
      }}
      title={[
        t('tpl_router.tpl_dashboard.accept_decline_modal.accept.title'),
        ` ${TicketContext.state}`,
      ]}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.accept_decline_modal.accept.button_ok')}
      cancelText={t('tpl_router.tpl_dashboard.accept_decline_modal.accept.button_cancel')}
      onOk={() => {
        form
          .validateFields()
          .then(
            values => {
              onOk(
                {
                  description: values.carrierComments || undefined,
                },
                values.driver?.value || undefined,
                values.truck?.value || undefined,
                values.trailer?.value || undefined,
              )
            },
            () => {
              notification.destroy('validationError')
              notification.error({
                message: t('globals.validation_error'),
                key: 'validationError',
              })
            },
          )

          .catch(() => {
            notification.destroy('validationError')
            notification.error({
              message: t('globals.validation_error'),
              key: 'validationError',
            })
          })
      }}
      afterClose={reset}
      destroyOnClose
      confirmLoading={loading}
      styles={{ body: { minHeight: '30rem' } }}
    >
      {ticketById?.ticket && !isTicketByIdLoading ? (
        <>
          <StyledForm form={form} initialValues={initialValues}>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item label={t('tpl_router.tpl_dashboard.columns.origin')} name="origin">
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.withdrawal_date')}
                name="date"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.supplier')}
                name="supplier"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.destination')}
                name="destination"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.purchase_order')}
                name="purchaseOrderAndEdd"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.material')}
                name="material"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('suppliers_router.new_ticket_modal.available')}
                name="availableQuantity"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row only-read">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.columns.equipment')}
                name="equipment"
              >
                <Input disabled />
              </StyledForm.Item>
            </StyledRow>
            {ticketById?.ticket?.comments && (
              <StyledRow className="carrier-form-row">
                <StyledForm.Item
                  label={t('tpl_router.tpl_dashboard.columns.comments')}
                  name="comments"
                >
                  <TextArea disabled rows={3} />
                </StyledForm.Item>
              </StyledRow>
            )}
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.driver')}
                name="driver"
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: requireDriver,
                    message: t('globals.generics.you_must_select_a_driver'),
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placeholder={t('globals.placeholder_select')}
                  filterOption={(input, option) => {
                    const driver = ticketById?.ticket?.carrier?.drivers?.find(
                      d => d.id === option?.value,
                    )
                    if (driver) {
                      const fullName =
                        `${driver.stateIdNumber} ${driver.firstName} ${driver.lastName}`.toLowerCase()
                      return fullName.includes(input.toLowerCase())
                    }
                    return false
                  }}
                  options={ticketById?.ticket?.carrier?.drivers?.map(driver => ({
                    label: driver.available
                      ? `${driver.stateIdNumber} - ${driver.firstName} ${driver.lastName}`
                      : `${driver.stateIdNumber} - ${driver.firstName} ${driver.lastName} (Ocupado)`,
                    value: driver.id,
                    available: driver.available,
                    disabled: !driver.available,
                  }))}
                  labelInValue
                  optionLabelProp="label"
                  onChange={handleDriverChange}
                />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.truck')}
                name="truck"
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                rules={[
                  {
                    required: !!selectedDriver && requireTruckAndTrailer,
                    message: 'Si selecciona chofer debe seleccionar un camión',
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placeholder={t('globals.placeholder_select')}
                  filterOption={(input, option) => {
                    const truck = ticketById?.ticket?.carrier?.trucks?.find(
                      tr => tr.id === option?.value,
                    )
                    if (truck) {
                      const labelTruck = truck.equipmentType
                        ? `${truck.licensePlate} ${truck.description} ${truck.equipmentType.nameShort}`.toLowerCase()
                        : `${truck.licensePlate} ${truck.description}`.toLowerCase()
                      return labelTruck.includes(input.toLowerCase())
                    }
                    return false
                  }}
                  options={ticketById?.ticket?.carrier?.trucks?.map(truck => {
                    if (truck.equipmentType) {
                      return {
                        label: truck.available
                          ? `${truck.licensePlate} - ${truck.description} - ${truck.equipmentType.nameShort}`
                          : `${truck.licensePlate} - ${truck.description} - ${truck.equipmentType.nameShort} (Ocupado)`,

                        value: truck.id,
                        available: truck.available,
                        disabled: !truck.available,
                      }
                    }
                    return {
                      label: truck.available
                        ? `${truck.licensePlate} - ${truck.description}`
                        : `${truck.licensePlate} - ${truck.description} (Ocupado)`,
                      value: truck.id,
                      available: truck.available,
                      disabled: !truck.available,
                    }
                  })}
                  labelInValue
                />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.trailer')}
                name="trailer"
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required:
                      !!selectedDriver &&
                      requireTruckAndTrailer &&
                      !ticketById?.ticket?.equipment?.straightTruck,
                    message: 'Si selecciona chofer debe seleccionar un acoplado',
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placeholder={
                    ticketById?.ticket?.equipment?.straightTruck
                      ? null
                      : t('globals.placeholder_select')
                  }
                  filterOption={(input, option) => {
                    const trailer = ticketById?.ticket?.carrier?.trailers?.find(
                      tr => tr.id === option?.value,
                    )
                    if (trailer) {
                      const labelTrailer =
                        `${trailer.licensePlate} ${trailer.description} ${trailer.equipmentType.nameShort}`.toLowerCase()
                      return labelTrailer.includes(input.toLowerCase())
                    }
                    return false
                  }}
                  options={ticketById?.ticket?.carrier?.trailers?.map(trailer => {
                    if (trailer.equipmentType) {
                      return {
                        label: trailer.available
                          ? `${trailer.licensePlate} - ${trailer.description} - ${trailer.equipmentType.nameShort}`
                          : `${trailer.licensePlate} - ${trailer.description} - ${trailer.equipmentType.nameShort} (Ocupado)`,

                        value: trailer.id,
                        available: trailer.available,
                        disabled: !trailer.available,
                      }
                    }
                    return {
                      label: trailer.available
                        ? `${trailer.licensePlate} - ${trailer.description}`
                        : `${trailer.licensePlate} - ${trailer.description} (Ocupado)`,
                      value: trailer.id,
                      available: trailer.available,
                      disabled: !trailer.available,
                    }
                  })}
                  labelInValue
                  disabled={ticketById?.ticket?.equipment?.straightTruck}
                />
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                label={t('tpl_router.tpl_dashboard.add_comment')}
                name="carrierComments"
              >
                <TextArea allowClear maxLength={1200} rows={3} />
              </StyledForm.Item>
            </StyledRow>
          </StyledForm>
          {ticketById?.ticket?.observations.map((observation: Note) => (
            <NoteElement key={observation.id} data={observation} />
          ))}
        </>
      ) : (
        <LoadingSpinner containerProps={{ style: { minHeight: '100%', height: '30rem' } }} />
      )}
    </Modal>
  )
}

export { AcceptTicketModal }
